import { useState, type ReactNode } from "react";
import { AlertTriangle, LifeBuoy } from "lucide-react";
import { cn } from "@/utils/helpers";
import { kitButtonClass } from "../../components/kit/Button";
import { CalendarConfirm, useCalendarViewport } from "../month/CalendarSheet";

/**
 * The operator's way out of a spinner that has stopped meaning anything.
 *
 * Every agent run in the calendar is fire-and-forget: a status is claimed, an
 * in-process promise does the work. A restart or an escaped throw leaves the
 * claim behind and the post, idea, brief or month sits in a machine state with
 * nothing behind it. Recovery does happen on its own (on boot and every ten
 * minutes), but "on its own" is not something a person watching a frozen card can
 * see, so this is the same escape hatch with a button on it.
 *
 * Shown only past the ten-minute floor — under that, a run really might still be
 * working and the honest answer is to wait.
 */

/** The warning every restart-shaped recovery has to make before it runs. */
const DOUBLE_SPEND_WARNING =
  "If that run is somehow still going, its work will be thrown away when it finishes — and the same work gets paid for twice.";

export interface StuckRecoveryCopy {
  /** Headline on the strip, e.g. "This research looks stuck". */
  title: string;
  /** What the button does, in the operator's terms. */
  body: string;
  /** Button label, e.g. "Restart research". */
  action: string;
  /** Modal heading. Defaults to the action. */
  confirmTitle?: string;
  /** What this specific recovery does. The double-spend line is appended for us. */
  confirmBody: string;
  /**
   * False for the recoveries that cannot double-spend — cancelling a rework and
   * condemning a brief both only free a row, they start nothing.
   */
  warnsDoubleSpend?: boolean;
}

/**
 * An amber card (radius 12, no hairline; style B) with one button, sitting on top of whatever the machine state
 * normally renders. It replaces nothing: the skeletons and tickers stay, because
 * the run might genuinely still land while the operator is reading this.
 */
export function StuckRecoveryStrip({
  copy,
  minutes,
  onConfirm,
  busy = false,
  className,
  children,
}: {
  copy: StuckRecoveryCopy;
  /** How long it has been sitting in this state. Named, not implied — it is the evidence. */
  minutes: number;
  onConfirm: () => void;
  busy?: boolean;
  className?: string;
  /** Extra detail rendered under the body copy, e.g. the recorded error. */
  children?: ReactNode;
}) {
  const [confirming, setConfirming] = useState(false);
  const warns = copy.warnsDoubleSpend !== false;
  const viewport = useCalendarViewport();

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-3 rounded-card bg-[color-mix(in_srgb,var(--co-attention)_10%,transparent)] p-3.5",
          className,
        )}
      >
        <div className="flex items-start gap-2.5">
          <AlertTriangle
            className="mt-0.5 size-4 shrink-0 text-[var(--co-attention)]"
            strokeWidth={2}
          />
          <div className="min-w-0 leading-tight">
            {/* Elapsed time, stated plainly rather than judged. The strip appears
                well before anything is provably wrong, so the number is evidence
                for the reader, not an accusation from us. */}
            <p className="text-[13px] font-medium text-co-text2">
              {copy.title} <span className="text-co-faint">· {minutes} min in this state</span>
            </p>
            <p className="mt-0.5 text-[12px] leading-relaxed text-co-muted">{copy.body}</p>
            {children}
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(true)}
          className={cn(kitButtonClass({ variant: "primary" }), "gap-1.5 self-start")}
        >
          <LifeBuoy className="size-3.5" /> {copy.action}
        </button>
      </div>

      {confirming && (
        <CalendarConfirm
          phone={viewport === "phone"}
          title={copy.confirmTitle ?? copy.action}
          description={copy.confirmBody}
          onClose={() => setConfirming(false)}
          busy={busy}
          tone="primary"
          cancelLabel="Keep waiting"
          confirmLabel={copy.action}
          onConfirm={() => {
            onConfirm();
            setConfirming(false);
          }}
        >
          {warns && (
            <p className="flex items-start gap-2.5 text-[14px] leading-[1.45] text-co-text2">
              <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-co-attention" strokeWidth={1.75} />
              <span>
                Nothing gets corrupted either way: the older run&apos;s result is simply discarded. {DOUBLE_SPEND_WARNING}
              </span>
            </p>
          )}
        </CalendarConfirm>
      )}
    </>
  );
}

export default StuckRecoveryStrip;
